/**
 * The client-side khata: the same core as the server, fed by the API and
 * cached in IndexedDB. Every screen reads from these signals; every write
 * goes through `submit`, which posts to the server and applies the returned
 * entry locally, so the screen updates the moment the server says yes.
 */
import { batch, computed, signal } from '@preact/signals';
import {
  apply,
  emptyState,
  fold,
  type Account,
  type CommandError,
  type Entry,
  type Item,
  type Master,
  type Party,
  type State,
} from '@khata/core';
import { api, ApiError, OfflineError, setUnauthorizedHandler } from './api.js';
import { clearCache, loadCache, saveEntries, saveMaster, type CachedMaster } from './db.js';
import { localToday } from './format.js';

export type AuthState = 'unknown' | 'setup' | 'login' | 'in';
export type SyncState = 'idle' | 'syncing' | 'offline' | 'error';

export const auth = signal<AuthState>('unknown');
export const sync = signal<SyncState>('idle');
export const ready = signal(false);
export const shopName = signal('');
export const today = signal(localToday());
export const version = signal('');

export interface User {
  id: string;
  login: string;
  name: string;
  role: 'owner' | 'staff';
  active: boolean;
}
/** Who is logged in on this device (null until the first sync after login). */
export const me = signal<User | null>(null);
export const users = signal<ReadonlyArray<User>>([]);
export const userName = (id: string | undefined): string =>
  (id && users.value.find((u) => u.id === id)?.name) || '';
/** Identity of the server's khata (from bootstrap). Cached entries are only valid for the same id. */
let khataId = '';

export const items = signal<ReadonlyMap<string, Item>>(new Map());
export const parties = signal<ReadonlyMap<string, Party>>(new Map());
export const accounts = signal<ReadonlyMap<string, Account>>(new Map());
export const entries = signal<ReadonlyArray<Entry>>([]);
export const state = signal<State>(emptyState());

export const master = computed<Master>(() => ({
  items: items.value,
  parties: parties.value,
  accounts: accounts.value,
}));

setInterval(() => {
  const t = localToday();
  if (t !== today.peek()) today.value = t;
}, 30_000);

interface Bootstrap {
  settings: { shopName: string };
  khataId?: string;
  items: Item[];
  parties: Party[];
  accounts: Account[];
  lastSeq: number;
  count: number;
  today: string;
  version: string;
  users?: User[];
  me?: User;
}

interface Status {
  setup: boolean;
  shopName: string;
  version: string;
}

function toMap<T extends { id: string }>(rows: ReadonlyArray<T>): Map<string, T> {
  return new Map(rows.map((r) => [r.id, r]));
}

function setMaster(m: CachedMaster): void {
  batch(() => {
    items.value = toMap(m.items);
    parties.value = toMap(m.parties);
    accounts.value = toMap(m.accounts);
    shopName.value = m.settings.shopName;
  });
}

function currentMaster(): CachedMaster {
  return {
    items: [...items.peek().values()],
    parties: [...parties.peek().values()],
    accounts: [...accounts.peek().values()],
    settings: { shopName: shopName.peek() },
    khataId,
  };
}

/** Apply one entry if it is the next one; returns false if we are out of sync. */
function applyEntry(e: Entry): boolean {
  const s = state.peek();
  if (e.seq <= s.lastSeq) return true; // already have it
  if (e.seq !== s.lastSeq + 1) return false;
  batch(() => {
    state.value = apply(s, e);
    entries.value = [...entries.peek(), e];
  });
  void saveEntries([e]);
  return true;
}

let syncing: Promise<void> | null = null;

/** Pull everything after our last seq. Safe to call often; concurrent calls share one run. */
export function pull(): Promise<void> {
  if (syncing) return syncing;
  syncing = (async () => {
    sync.value = 'syncing';
    try {
      const boot = await api.get<Bootstrap>('/api/bootstrap');
      version.value = boot.version;
      batch(() => {
        users.value = boot.users ?? [];
        me.value = boot.me ?? null;
      });
      const serverId = boot.khataId ?? '';
      if (serverId !== khataId || boot.lastSeq < state.peek().lastSeq) {
        // Another khata (reseeded, reset or restored from a backup): our cached entries are not its entries.
        await clearCache();
        batch(() => {
          state.value = emptyState();
          entries.value = [];
        });
      }
      khataId = serverId;
      setMaster({
        items: boot.items,
        parties: boot.parties,
        accounts: boot.accounts,
        settings: boot.settings,
      });
      void saveMaster(currentMaster());
      let after = state.peek().lastSeq;
      while (after < boot.lastSeq) {
        const page = await api.get<{ entries: Entry[]; lastSeq: number }>(
          `/api/entries?after=${after}&limit=5000`,
        );
        if (page.entries.length === 0) break;
        const s = fold(page.entries, state.peek());
        batch(() => {
          state.value = s;
          entries.value = [...entries.peek(), ...page.entries];
        });
        void saveEntries(page.entries);
        after = s.lastSeq;
      }
      sync.value = 'idle';
      auth.value = 'in';
    } catch (e) {
      if (e instanceof OfflineError) sync.value = 'offline';
      else if (e instanceof ApiError && e.status === 401) sync.value = 'idle';
      else {
        sync.value = 'error';
        console.error(e);
      }
    } finally {
      syncing = null;
      ready.value = true;
    }
  })();
  return syncing;
}

// ---------------------------------------------------------------------------
// Live updates
// ---------------------------------------------------------------------------

let source: EventSource | null = null;
let retry = 1000;

function listen(): void {
  if (source || typeof EventSource === 'undefined') return;
  const es = new EventSource('/api/events');
  source = es;
  es.addEventListener('hello', () => {
    retry = 1000;
    void pull();
  });
  es.addEventListener('entry', (ev) => {
    const { entry } = JSON.parse((ev as MessageEvent).data as string) as { entry: Entry };
    if (!applyEntry(entry)) void pull();
  });
  const onMaster = (ev: Event) => {
    const data = JSON.parse((ev as MessageEvent).data as string) as
      | { kind: 'item'; item: Item }
      | { kind: 'party'; party: Party }
      | { kind: 'account'; account: Account }
      | { kind: 'deleted'; table: 'items' | 'parties' | 'accounts'; id: string }
      | { kind: 'settings'; settings: { shopName: string } };
    if (data.kind === 'item') items.value = new Map(items.peek()).set(data.item.id, data.item);
    else if (data.kind === 'party')
      parties.value = new Map(parties.peek()).set(data.party.id, data.party);
    else if (data.kind === 'account')
      accounts.value = new Map(accounts.peek()).set(data.account.id, data.account);
    else if (data.kind === 'settings') shopName.value = data.settings.shopName;
    else if (data.kind === 'deleted') {
      const sig = data.table === 'items' ? items : data.table === 'parties' ? parties : accounts;
      const next = new Map(sig.peek() as Map<string, never>);
      next.delete(data.id);
      (sig as typeof items).value = next as never;
    }
    void saveMaster(currentMaster());
  };
  for (const kind of ['item', 'party', 'account', 'deleted', 'settings'])
    es.addEventListener(kind, onMaster);
  es.onerror = () => {
    es.close();
    source = null;
    if (auth.peek() !== 'in') return;
    setTimeout(listen, retry);
    retry = Math.min(retry * 2, 30_000);
  };
}

export function stopListening(): void {
  source?.close();
  source = null;
}

// ---------------------------------------------------------------------------
// Boot and auth
// ---------------------------------------------------------------------------

setUnauthorizedHandler((reason) => {
  auth.value = reason;
  stopListening();
});

export async function boot(): Promise<void> {
  const cached = await loadCache();
  if (cached) {
    if (cached.master) {
      setMaster(cached.master);
      khataId = cached.master.khataId ?? '';
    }
    if (cached.entries.length) {
      try {
        const s = fold(cached.entries);
        batch(() => {
          state.value = s;
          entries.value = cached.entries;
        });
      } catch (e) {
        console.error('cache is inconsistent, dropping it', e);
        await clearCache();
      }
    }
  }
  try {
    const status = await api.get<Status>('/api/status');
    version.value = status.version;
    if (!status.setup) {
      auth.value = 'setup';
      ready.value = true;
      return;
    }
    if (status.shopName) shopName.value = status.shopName;
  } catch (e) {
    if (e instanceof OfflineError) {
      sync.value = 'offline';
      auth.value = entries.peek().length ? 'in' : 'login';
      ready.value = true;
      return;
    }
  }
  await pull();
  if (auth.peek() === 'in') listen();
  else if (auth.peek() === 'unknown') auth.value = 'login';
}

const LOGIN_KEY = 'khata.login';

/** The login id used last on this device, so the next time only the PIN is needed. */
export function lastLogin(): string {
  try {
    return localStorage.getItem(LOGIN_KEY) ?? '';
  } catch {
    return '';
  }
}

function rememberLogin(id: string): void {
  try {
    localStorage.setItem(LOGIN_KEY, id);
  } catch {
    // private mode: nothing to remember
  }
}

export async function login(loginId: string, pin: string): Promise<void> {
  const r = await api.post<{ user: User }>('/api/login', {
    login: loginId,
    pin,
    label: navigator.userAgent.slice(0, 60),
  });
  me.value = r.user;
  rememberLogin(loginId.trim());
  auth.value = 'in';
  await pull();
  listen();
}

export async function setup(input: {
  login: string;
  name: string;
  pin: string;
  shopName: string;
}): Promise<void> {
  const r = await api.post<{ user: User }>('/api/setup', input);
  me.value = r.user;
  rememberLogin(input.login.trim());
  auth.value = 'in';
  await pull();
  listen();
}

export async function logout(): Promise<void> {
  try {
    await api.post('/api/logout', {});
  } catch {
    // leaving anyway
  }
  stopListening();
  await clearCache();
  khataId = '';
  batch(() => {
    state.value = emptyState();
    entries.value = [];
    items.value = new Map();
    parties.value = new Map();
    accounts.value = new Map();
    me.value = null;
    users.value = [];
    auth.value = 'login';
  });
}

// ---------------------------------------------------------------------------
// Writes
// ---------------------------------------------------------------------------

export type SubmitResult =
  | { ok: true; entry: Entry }
  | { ok: false; error: CommandError | { code: string; message: string; field?: string } };

/** Record an entry. Validation errors come back as a result; network problems throw. */
export async function submit(type: string, input: unknown): Promise<SubmitResult> {
  try {
    const r = await api.post<{ entry: Entry }>('/api/entries', { type, input });
    if (!applyEntry(r.entry)) await pull();
    return { ok: true, entry: r.entry };
  } catch (e) {
    if (e instanceof ApiError && (e.status === 400 || e.status === 404 || e.status === 409))
      return { ok: false, error: e.body };
    throw e;
  }
}

type MasterResult<T> =
  { ok: true; value: T } | { ok: false; error: { code: string; message: string; field?: string } };

async function masterCall<T>(fn: () => Promise<T>): Promise<MasterResult<T>> {
  try {
    return { ok: true, value: await fn() };
  } catch (e) {
    if (e instanceof ApiError && (e.status === 400 || e.status === 404 || e.status === 409))
      return { ok: false, error: e.body };
    throw e;
  }
}

export function createItem(
  input: unknown,
  opening?: { date: string; qty: number; unitCost: number },
): Promise<MasterResult<{ item: Item; entry: Entry | null }>> {
  return masterCall(async () => {
    const r = await api.post<{ item: Item; entry: Entry | null }>('/api/items', {
      ...(input as object),
      opening,
    });
    items.value = new Map(items.peek()).set(r.item.id, r.item);
    if (r.entry && !applyEntry(r.entry)) await pull();
    void saveMaster(currentMaster());
    return r;
  });
}

export function updateItem(id: string, input: unknown): Promise<MasterResult<Item>> {
  return masterCall(async () => {
    const item = await api.put<Item>(`/api/items/${encodeURIComponent(id)}`, input);
    items.value = new Map(items.peek()).set(item.id, item);
    void saveMaster(currentMaster());
    return item;
  });
}

export function deleteItem(
  id: string,
): Promise<MasterResult<{ result: 'deleted' | 'archived'; item: Item | null }>> {
  return masterCall(async () => {
    const r = await api.delete<{ result: 'deleted' | 'archived'; item: Item | null }>(
      `/api/items/${encodeURIComponent(id)}`,
    );
    const next = new Map(items.peek());
    if (r.item) next.set(id, r.item);
    else next.delete(id);
    items.value = next;
    void saveMaster(currentMaster());
    return r;
  });
}

export function createParty(
  input: unknown,
  opening?: { date: string; amount: number },
): Promise<MasterResult<{ party: Party; entry: Entry | null }>> {
  return masterCall(async () => {
    const r = await api.post<{ party: Party; entry: Entry | null }>('/api/parties', {
      ...(input as object),
      opening,
    });
    parties.value = new Map(parties.peek()).set(r.party.id, r.party);
    if (r.entry && !applyEntry(r.entry)) await pull();
    void saveMaster(currentMaster());
    return r;
  });
}

export function updateParty(id: string, input: unknown): Promise<MasterResult<Party>> {
  return masterCall(async () => {
    const party = await api.put<Party>(`/api/parties/${encodeURIComponent(id)}`, input);
    parties.value = new Map(parties.peek()).set(party.id, party);
    void saveMaster(currentMaster());
    return party;
  });
}

export function deleteParty(
  id: string,
): Promise<MasterResult<{ result: 'deleted' | 'archived'; party: Party | null }>> {
  return masterCall(async () => {
    const r = await api.delete<{ result: 'deleted' | 'archived'; party: Party | null }>(
      `/api/parties/${encodeURIComponent(id)}`,
    );
    const next = new Map(parties.peek());
    if (r.party) next.set(id, r.party);
    else next.delete(id);
    parties.value = next;
    void saveMaster(currentMaster());
    return r;
  });
}

export function createAccount(
  input: unknown,
  opening?: { date: string; amount: number },
): Promise<MasterResult<{ account: Account; entry: Entry | null }>> {
  return masterCall(async () => {
    const r = await api.post<{ account: Account; entry: Entry | null }>('/api/accounts', {
      ...(input as object),
      opening,
    });
    accounts.value = new Map(accounts.peek()).set(r.account.id, r.account);
    if (r.entry && !applyEntry(r.entry)) await pull();
    void saveMaster(currentMaster());
    return r;
  });
}

export function updateAccount(id: string, input: unknown): Promise<MasterResult<Account>> {
  return masterCall(async () => {
    const account = await api.put<Account>(`/api/accounts/${encodeURIComponent(id)}`, input);
    accounts.value = new Map(accounts.peek()).set(account.id, account);
    void saveMaster(currentMaster());
    return account;
  });
}

export function updateSettings(input: {
  shopName: string;
}): Promise<MasterResult<{ shopName: string }>> {
  return masterCall(async () => {
    const s = await api.put<{ shopName: string }>('/api/settings', input);
    shopName.value = s.shopName;
    void saveMaster(currentMaster());
    return s;
  });
}

export function changePin(pin: string, newPin: string): Promise<MasterResult<{ ok: true }>> {
  return masterCall(() => api.post<{ ok: true }>('/api/pin', { pin, newPin }));
}

export function addUser(input: {
  login: string;
  name: string;
  pin: string;
}): Promise<MasterResult<User>> {
  return masterCall(async () => {
    const u = await api.post<User>('/api/users', input);
    users.value = [...users.peek().filter((x) => x.id !== u.id), u];
    return u;
  });
}

export function updateUser(
  id: string,
  input: { name?: string; active?: boolean; pin?: string },
): Promise<MasterResult<User>> {
  return masterCall(async () => {
    const u = await api.put<User>(`/api/users/${id}`, input);
    users.value = users.peek().map((x) => (x.id === u.id ? u : x));
    if (me.peek()?.id === u.id) me.value = u;
    return u;
  });
}
